/**
 * HTTP client for the Shieldz Payments merchant dashboard.
 *
 * All `/api/admin/*` calls go through `adminFetch()` which attaches the
 * Telegram WebApp initData header. The bot validates the HMAC against the
 * bot token (see teleswap-bot/src/server/init-data.ts) on every request.
 *
 * `/api/v1/*` Bearer-API-key calls are dashboard-side ONLY in a few
 * cases (webhook config, deliveries list, manual retry — merchants
 * always own these via their own keys). For those we expose
 * `apiKeyFetch()` that takes a key directly.
 *
 * Mirrors the spec in teleswap-bot/docs/SHIELDZ_PAYMENTS_FRONTEND_SPEC.md.
 */

export const API_BASE: string =
  (import.meta.env.VITE_SHIELDZ_API_URL as string | undefined) ??
  "https://telegram-wallet.shieldz.cash";

export const getInitData = (): string => {
  const w = window as unknown as { Telegram?: { WebApp?: { initData?: string } } };
  return w.Telegram?.WebApp?.initData ?? "";
};

export const getSessionToken = (): string => {
  try {
    return localStorage.getItem("shieldz.session") ?? "";
  } catch {
    return "";
  }
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly param?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const parseErrorEnvelope = async (
  res: Response,
): Promise<ApiError> => {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // not JSON
  }
  if (
    body &&
    typeof body === "object" &&
    "error" in body &&
    body.error &&
    typeof body.error === "object"
  ) {
    const e = body.error as {
      type?: string;
      code?: string;
      message?: string;
      param?: string;
    };
    return new ApiError(
      res.status,
      e.code ?? "unknown_error",
      e.message ?? `HTTP ${res.status}`,
      e.param,
    );
  }
  return new ApiError(res.status, "unknown_error", `HTTP ${res.status}`);
};

const baseFetch = async <T>(
  path: string,
  init: RequestInit & { headers?: Record<string, string> } = {},
): Promise<T> => {
  const url = `${API_BASE.replace(/\/+$/, "")}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      // Let the browser set the multipart boundary for FormData uploads.
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) throw await parseErrorEnvelope(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
};

/**
 * Admin auth: prefers a Bearer session (web wallet sign-in) and falls
 * back to Telegram-Init-Data when the dashboard is opened inside a TMA.
 * The backend's `authAdmin()` accepts either; we just attach whichever
 * we have. Bearer wins if both are present (lets a TMA session swap to
 * a wallet session on the same device without a logout).
 */
export const adminFetch = <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  const session = getSessionToken();
  if (session) {
    headers.Authorization = `Bearer ${session}`;
  } else {
    headers["Telegram-Init-Data"] = getInitData();
  }
  return baseFetch<T>(path, { ...init, headers });
};

export const apiKeyFetch = <T>(
  path: string,
  apiKey: string,
  init: RequestInit = {},
): Promise<T> =>
  baseFetch<T>(path, {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${apiKey}`,
    },
  });

/**
 * Download the invoices CSV export. Admin auth is header-based (Bearer session
 * or Telegram-Init-Data), so a plain <a download> can't carry it — we fetch
 * with the auth header, then trigger a browser download from the blob.
 */
export const downloadInvoicesCsv = async (params: {
  from?: number;
  to?: number;
  status?: string;
  mode?: string;
}): Promise<void> => {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", String(params.from));
  if (params.to) qs.set("to", String(params.to));
  if (params.status) qs.set("status", params.status);
  if (params.mode) qs.set("mode", params.mode);
  const url = `${API_BASE.replace(/\/+$/, "")}/api/admin/invoices/export.csv?${qs.toString()}`;
  const headers: Record<string, string> = {};
  const session = getSessionToken();
  if (session) headers.Authorization = `Bearer ${session}`;
  else headers["Telegram-Init-Data"] = getInitData();
  const res = await fetch(url, { headers });
  if (!res.ok) throw await parseErrorEnvelope(res);
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m?.[1] ?? "shieldz-invoices.csv";
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

// ─── Types (mirror SHIELDZ_PAYMENTS_FRONTEND_SPEC.md §9) ─────────────────

export type InvoiceStatus = "pending" | "paid" | "expired" | "failed";
export type ApiKeyMode = "live" | "test";

export interface Invoice {
  id: string;
  object: "invoice";
  amount_usd_cents: number;
  memo: string | null;
  settlement: {
    chain: string;
    asset: string;
    address: string;
  };
  customer_email: string | null;
  metadata: unknown | null;
  /** Priced checkouts: what was bought, priced from the catalog. */
  line_items?: Array<{
    price: string | null;
    product: string | null;
    name: string;
    quantity: number;
    unit_amount_usd_cents: number;
    amount_usd_cents: number;
  }> | null;
  /** The payment link it came from (plink_…). */
  payment_link?: string | null;
  /** Only returned to the shop itself. */
  shipping?: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  } | null;
  client_reference_id?: string | null;
  status: InvoiceStatus;
  expires_at: number;
  paid_after_expiry: boolean;
  created_at: number;
  paid_at: number | null;
  mode: ApiKeyMode;
  pay_url: string;
  quote?: InvoiceQuote | null;
  /** V1 forwarder block — present when the invoice was created via the
   *  Zcash pure-forwarder path (UFVK + diversifier). Either this or the
   *  generic `payment` block is set; sometimes both for ZEC invoices. */
  zcash?: {
    u1_address: string;
    amount_zec_atomic: string;
    zec_usd_rate_at_creation: number | null;
  } | null;
  /** Multichain V1 — the "primary" per-invoice payment target. Set on
   *  every new invoice as a hint for which chain to default the
   *  checkout picker to. The full per-chain list is `payment_targets`
   *  below. */
  payment_chain_id?: string | null;
  payment_address?: string | null;
  payment_amount_atomic?: string | null;
  /** Multichain V1.1 — full set of per-chain targets. Buyer picks one at
   *  checkout. Empty array on legacy pre-V1.1 invoices. */
  payment_targets?: PaymentTarget[];
  /** V1.4.4 — the chain + asset the buyer ACTUALLY paid on. Computed
   *  server-side from the matched payment row. Null when status != 'paid'
   *  OR when we can't derive (legacy single-chain rows fall back to
   *  `payment_chain_id`). Use this for revenue attribution; ignore the
   *  legacy `payment_chain_id` for paid invoices. */
  paid_on?: {
    chain_id: string;
    asset_symbol: string | null;
    tx_hash: string | null;
  } | null;
}

/** One row of `invoice_payment_targets`. */
export interface PaymentTarget {
  chain_id: string;
  /** V1.3 — ERC-20 symbol on EVM rows ("USDC" | "USDT"); null on
   *  native chains (ZEC, BTC). */
  asset_symbol: string | null;
  address: string;
  /** Big-int as decimal string, in the chain's atomic unit. */
  amount_atomic: string;
}

export interface InvoiceQuote {
  amount_zec_atomic: string;
  expected_out_atomic: string;
  expected_out_usd: number;
  deposit_address: string;
  deposit_memo: string;
  fees: unknown[];
  expires_at_ms: number;
  ttl_seconds: number;
}

export interface MerchantProfile {
  object: "merchant";
  exists: boolean;
  tg_user_id?: number;
  display_name?: string;
  logo_url?: string | null;
  /** White-label accent (#RRGGBB) applied across the checkout. Null = default. */
  accent?: string | null;
  /** Legacy settlement target — unused under V1 forwarder. */
  default_settlement?: { chain: string; asset: string; address: string };
  slippage_bps?: number;
  setup_verified?: boolean;
  enabled?: boolean;
  telegram_dm_enabled?: boolean;
  /** V1.6 — true once the merchant has linked a real Telegram chat (via the
   *  connect deep-link). DMs only deliver when this is true. */
  telegram_connected?: boolean;
  /** V1.6 — true for the Shieldz platform account (unlocks the Ads page). */
  is_platform_admin?: boolean;
  /** V1.5 — "Pay with any crypto" opt-in. Defaults OFF (unlike DM, which
   *  defaults ON), so the UI must treat `undefined` as false. */
  any_crypto_enabled?: boolean;
  /** V1.6 — when a swap-in coin can't be quoted (too small / no route),
   *  receive it DIRECTLY to your own wallet instead of failing the payment.
   *  Only applies to coins on rails we can watch (USDC/USDT on EVM, native
   *  BTC/DOGE via xpub). Defaults OFF; undefined = false. */
  direct_fallback_enabled?: boolean;
  /** Confidential (NEAR shielded) routing preference. "preferred" asks for a
   *  private route and falls back to standard when a pair can't be shielded;
   *  "only" fails a coin that can't be paid privately. Defaults "off". */
  confidential_mode?: "off" | "preferred" | "only";
  /** V1.5 — per-coin refund addresses for swap-in coins on chains not
   *  otherwise configured (LTC/DASH/NEAR), keyed by chain id. */
  swap_refund_addresses?: Record<string, string>;
  created_at?: number;
  updated_at?: number;
  /** Present when the merchant has registered a UFVK (V1 forwarder).
   *  The raw UFVK is never returned — only the derived Sapling IVK as
   *  a 64-char hex string, so the dashboard can confirm a UFVK is on
   *  file without exposing it. */
  zcash?: {
    has_sapling: boolean;
    sapling_ivk_hex?: string;
  } | null;
  /** Optional EVM address the merchant prefers for ZEC→USDC swaps via
   *  LeoDex. Pre-fills the conversion deeplink when set. */
  preferred_conversion_address?: string | null;
  /** Multichain V1: list of configured chains for this merchant. The
   *  raw primitive value (UFVK / xpub / address) is never exposed —
   *  only a fingerprint suitable for UI display. */
  chains?: Array<{
    chain_id: string;
    primitive_kind: "ufvk" | "xpub" | "address";
    primitive_fingerprint: string;
    enabled: boolean;
    created_at: number;
  }>;
}

export interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  last4: string;
  mode: ApiKeyMode;
  revoked_at: number | null;
  last_used_at: number | null;
  created_at: number;
}

export interface MintedApiKey extends ApiKey {
  object: "api_key";
  raw_key: string;
}

export interface WebhookEndpoint {
  url: string | null;
  signing_secret_prefix: string | null;
  enabled?: boolean;
  updated_at?: number;
}

export interface WebhookDelivery {
  id: number;
  invoice_id: number;
  event_type: "invoice.paid" | "invoice.failed" | "invoice.expired";
  attempt: number;
  status: "pending" | "delivered" | "dead" | "manual_retry";
  http_status_code: number | null;
  next_attempt_at: number | null;
  created_at: number;
  delivered_at: number | null;
}

// ─── Admin: merchant lifecycle ───────────────────────────────────────────

export const getMyMerchant = (): Promise<MerchantProfile> =>
  adminFetch<MerchantProfile>("/api/admin/merchants/me");

export interface PlatformStats {
  total_users: number;
  total_merchants: number;
  total_invoices: number;
  paid_invoices: number;
  pending_invoices: number;
  total_volume_usd: string;
  total_pages: number;
  active_api_keys: number;
  keyless_accounts: number;
  claimed_accounts: number;
  demo_attempts: number;
  /** Last 7 days of signups, oldest first. Optional for backward-compat. */
  signups_daily?: Array<{ date: string; merchants: number; keyless: number }>;
  /** Checkout-side quote/deposit failures in the last 24h, grouped by cause. */
  checkout_errors_24h?: Array<{
    event: string;
    asset_id: string | null;
    protocol: string | null;
    reason: string | null;
    n: number;
  }>;
  merchants: Array<{
    id: number;
    display_name: string;
    logo_url: string | null;
    created_at: number;
    invoice_count: number;
    paid_count: number;
    volume_usd: string;
  }>;
}

export interface PlatformMerchantInvoice {
  public_id: string;
  created_at: number;
  amount_usd: string;
  memo: string | null;
  status: string;
  mode: string;
  settlement_chain: string;
  settlement_asset: string;
  customer_email: string | null;
  source: string | null;
  paid_at: number | null;
  expires_at: number;
}

/** Platform-wide stats. Admin-only; the backend hard-gates with 403. */
export const getPlatformStats = (): Promise<PlatformStats> =>
  adminFetch<PlatformStats>("/api/admin/platform-stats");

/** Per-merchant invoice drill-down for the admin stats page (newest 100). */
export const getPlatformMerchantInvoices = (merchantId: number): Promise<{ invoices: PlatformMerchantInvoice[] }> =>
  adminFetch<{ invoices: PlatformMerchantInvoice[] }>(
    `/api/admin/platform-merchant-invoices?merchant_id=${merchantId}`,
  );

/**
 * Claim a keyless payment-link account into the signed-in account. The
 * manage_token is the capability secret from the /a/<token> link; the backend
 * binds the link's merchant to this account and marks it claimed. Requires a
 * Bearer session (adminFetch attaches it).
 */
export const claimLink = (
  manageToken: string,
): Promise<{ object: "claim"; claimed: boolean; merchant_id: number; dashboard_url: string }> =>
  adminFetch("/api/v1/links/claim", {
    method: "POST",
    body: JSON.stringify({ manage_token: manageToken }),
  });

export interface ChainConfigInput {
  /** "ZEC" | "BTC" | "BASE" | "ARB" | "OP" | "POLY" | "ETH" */
  chain_id: string;
  /** "ufvk" | "xpub" | "address" */
  primitive_kind: "ufvk" | "xpub" | "address";
  primitive_value: string;
}

export interface ChainConfigResult {
  chain_id: string;
  status: "ok" | "error";
  reason?: string;
}

export interface UpsertMerchantResponse {
  object: "merchant";
  tg_user_id: number;
  display_name: string;
  default_settlement?: { chain: string; asset: string; address: string };
  setup_verified: boolean;
  enabled: boolean;
  zcash?: { has_sapling: boolean; sapling_ivk_hex?: string } | null;
  /** Per-chain status from the multichain `chains` array in the request. */
  chains?: ChainConfigResult[];
}

export const upsertMyMerchant = (body: {
  display_name: string;
  logo_url?: string | null;
  /** White-label accent (#RRGGBB). Empty string or null clears it. */
  accent?: string | null;
  /** Legacy settlement block — optional under V1 forwarder. */
  settlement?: { chain: string; asset: string; address: string };
  slippage_bps?: number;
  /** V1 pure forwarder shortcut: merchant's Unified Full Viewing Key.
   *  Equivalent to passing a `chains` entry with chain_id="ZEC",
   *  primitive_kind="ufvk". Kept for back-compat. */
  zcash_ufvk?: string;
  /** Multichain V1: array of per-chain receive configurations. The
   *  backend validates each via the chain's ChainDriver and persists
   *  to `merchant_chain_configs`. Each entry's status is returned in
   *  the response's `chains` field. */
  chains?: ChainConfigInput[];
}): Promise<UpsertMerchantResponse> =>
  adminFetch<UpsertMerchantResponse>("/api/admin/merchants", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const verifySettlementSetup = (): Promise<{ setup_verified: true }> =>
  adminFetch("/api/admin/merchants/me/verify", { method: "POST" });

/** Upload the merchant's checkout logo (PNG/JPEG/WebP, ≤2 MB). Returns the
 *  stored URL, which the backend also writes into the merchant's logo_url. */
export const uploadMerchantLogo = (file: File): Promise<{ logo_url: string }> => {
  const fd = new FormData();
  fd.append("file", file);
  return adminFetch("/api/admin/merchants/me/logo", { method: "POST", body: fd });
};

/** Remove the merchant's custom logo. */
export const deleteMerchantLogo = (): Promise<{ logo_url: null }> =>
  adminFetch("/api/admin/merchants/me/logo", { method: "DELETE" });

export const setNotificationsEnabled = (
  enabled: boolean,
): Promise<{ telegram_dm_enabled: boolean }> =>
  adminFetch("/api/admin/merchants/me/notifications", {
    method: "PUT",
    body: JSON.stringify({ telegram_dm_enabled: enabled }),
  });

/** Start the Telegram connect flow — returns a t.me deep link the merchant
 *  opens; tapping Start in the bot captures their real chat for DMs. */
export const createTelegramLink = (): Promise<{ url: string }> =>
  adminFetch("/api/admin/merchants/me/telegram-link", { method: "POST" });

/** Disconnect Telegram DMs (clears the linked chat). */
export const disconnectTelegram = (): Promise<{ telegram_connected: false }> =>
  adminFetch("/api/admin/merchants/me/telegram-link", { method: "DELETE" });

/** V1.6 — per-channel notification preferences. */
export type NotifyEvent = "paid" | "failed" | "expired";

export interface NotificationPrefs {
  events: NotifyEvent[]; // all selectable event keys (server-driven)
  telegram_dm_enabled: boolean; // the Telegram master switch
  telegram_events: NotifyEvent[];
  discord_enabled: boolean;
  discord_webhook_url: string | null;
  discord_events: NotifyEvent[];
}

export const getNotificationPrefs = (): Promise<NotificationPrefs> =>
  adminFetch<NotificationPrefs>("/api/admin/merchants/me/notification-prefs");

/** Update per-channel masks + Discord webhook. Any field may be omitted. */
export const updateNotificationPrefs = (body: {
  telegram_events?: NotifyEvent[];
  discord_enabled?: boolean;
  discord_webhook_url?: string | null;
  discord_events?: NotifyEvent[];
}): Promise<{
  telegram_events: NotifyEvent[];
  discord_enabled: boolean;
  discord_webhook_url: string | null;
  discord_events: NotifyEvent[];
}> =>
  adminFetch("/api/admin/merchants/me/notification-prefs", {
    method: "PUT",
    body: JSON.stringify(body),
  });

/** Fire a sample Discord message to verify a webhook URL works. Pass the URL
 *  to test it before saving; omit to test the saved one. */
export const testDiscordNotification = (
  discord_webhook_url?: string,
): Promise<{ ok: true }> =>
  adminFetch("/api/admin/merchants/me/notification-prefs/test", {
    method: "POST",
    body: JSON.stringify(discord_webhook_url ? { discord_webhook_url } : {}),
  });

/** V1.5 — toggle "Pay with any crypto" (swap-settled deposit payments). */
export const setAnyCryptoEnabled = (
  enabled: boolean,
): Promise<{ any_crypto_enabled: boolean }> =>
  adminFetch("/api/admin/merchants/me/any-crypto", {
    method: "PUT",
    body: JSON.stringify({ any_crypto_enabled: enabled }),
  });

/** V1.6 — toggle "direct fallback": when a swap-in coin can't be quoted,
 *  receive it directly to the merchant's wallet (watchable rails only). */
export const setDirectFallbackEnabled = (
  enabled: boolean,
): Promise<{ direct_fallback_enabled: boolean }> =>
  adminFetch("/api/admin/merchants/me/direct-fallback", {
    method: "PUT",
    body: JSON.stringify({ direct_fallback_enabled: enabled }),
  });

/** Set the confidential (NEAR shielded) routing preference. */
export const setConfidentialMode = (
  mode: "off" | "preferred" | "only",
): Promise<{ confidential_mode: "off" | "preferred" | "only" }> =>
  adminFetch("/api/admin/merchants/me/confidential-mode", {
    method: "PUT",
    body: JSON.stringify({ confidential_mode: mode }),
  });

/** V1.5 — set per-coin refund addresses (LTC/DASH/NEAR). Partial map; pass
 *  null/"" to clear a coin. Returns the full updated map. */
export const setSwapRefundAddresses = (
  addrs: Record<string, string | null>,
): Promise<{ swap_refund_addresses: Record<string, string> }> =>
  adminFetch("/api/admin/merchants/me/swap-refund-addresses", {
    method: "PUT",
    body: JSON.stringify(addrs),
  });

/** Enable or disable a configured chain (pay-in coin or receive chain).
 *  Disabling is soft (preserves the stored primitive + cursor). The backend
 *  refuses to disable the chain the settlement token currently lives on. */
export const setChainEnabled = (
  chainId: string,
  enabled: boolean,
): Promise<{ ok: boolean; chain: string; enabled: boolean }> =>
  adminFetch(
    `/api/admin/merchants/me/chains/${encodeURIComponent(chainId)}/enabled`,
    { method: "POST", body: JSON.stringify({ enabled }) },
  );

/**
 * Consumer-side invoice creation — Telegram-Init-Data auth, no Bearer key
 * needed. For the standard dashboard flow ("tap + Create payment link").
 * Developer-mode integrations still use POST /api/v1/invoices with Bearer.
 */
export const createInvoiceAdmin = (body: {
  amount_usd_cents: number;
  memo?: string | null;
  customer_email?: string | null;
  expires_in_seconds?: number;
  metadata?: unknown;
  settlement?: { chain: string; asset: string; address: string };
  /** Multichain V1: which configured chain the buyer pays via. Defaults
   *  to the merchant's primary (ZEC preferred, else first configured). */
  payment_chain?: string;
}): Promise<Invoice> =>
  adminFetch<Invoice>("/api/admin/invoices", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const listInvoicesAdmin = (
  opts: { status?: InvoiceStatus; starting_after?: number; limit?: number } = {},
): Promise<{ object: "list"; data: Invoice[]; has_more: boolean }> => {
  const qs = new URLSearchParams();
  if (opts.status) qs.set("status", opts.status);
  if (opts.starting_after) qs.set("starting_after", String(opts.starting_after));
  if (opts.limit) qs.set("limit", String(opts.limit));
  const path = "/api/admin/invoices" + (qs.toString() ? `?${qs}` : "");
  return adminFetch(path);
};

// ─── V1.4.5: Unattributed deposits ───────────────────────────────────────
// On-chain deposits the watcher saw to the merchant's receive address but
// couldn't exact-amount-match to a pending invoice. Surfaced so the merchant
// can resolve them (manual match / refund / mark as misc).

export interface UnattributedDeposit {
  id: number;
  chain_id: string;
  asset_symbol: string | null;
  receiver_address: string;
  sender_address: string | null;
  amount_atomic: string;
  tx_hash: string;
  block_height: number | null;
  detected_at: number;
  candidate: {
    invoice_id: number;
    invoice_public_id: string;
    amount_usd_cents: number;
    memo: string | null;
  } | null;
}

export const listUnattributedDeposits = (): Promise<{
  object: "list";
  data: UnattributedDeposit[];
}> => adminFetch("/api/admin/unattributed-deposits");

export const matchUnattributedDeposit = (
  id: number,
  invoiceId: number,
): Promise<{ ok: true; invoice_public_id: string }> =>
  adminFetch(`/api/admin/unattributed-deposits/${id}/match`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invoice_id: invoiceId }),
  });

export const ignoreUnattributedDeposit = (
  id: number,
  action: "misc" | "ignored" | "refunded" = "ignored",
): Promise<{ ok: true }> =>
  adminFetch(`/api/admin/unattributed-deposits/${id}/ignore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });

// ─── Admin: API keys ─────────────────────────────────────────────────────

export const listApiKeys = (): Promise<{
  object: "list";
  data: ApiKey[];
}> => adminFetch("/api/admin/api_keys");

export const mintApiKey = (body: {
  name: string;
  mode: ApiKeyMode;
}): Promise<MintedApiKey> =>
  adminFetch<MintedApiKey>("/api/admin/api_keys", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const revokeApiKey = (id: number): Promise<{ id: number; revoked: true }> =>
  adminFetch(`/api/admin/api_keys/${id}/revoke`, { method: "POST" });

// ─── Bearer-API-key endpoints used by the dashboard ──────────────────────
// (Webhook config + deliveries — merchant owns these via their own key.)

// The exact REST call the Quick Start curl makes: POST /api/v1/invoices with
// a Bearer key. Used by the "Try it" button so the sample runs for real.
export const createInvoiceWithKey = (
  apiKey: string,
  body: {
    amount_usd_cents: number;
    memo?: string | null;
    customer_email?: string | null;
    metadata?: unknown;
  },
): Promise<Invoice> =>
  apiKeyFetch<Invoice>("/api/v1/invoices", apiKey, {
    method: "POST",
    body: JSON.stringify(body),
  });

// Simulate a paid invoice (test mode only) — marks it paid and enqueues a
// real invoice.paid webhook, exactly like a settlement would. Powers the
// Quick Start "Finalize payment" button. Needs a webhook endpoint configured.
export const simulateTestPayment = (
  invoiceId: string,
): Promise<{
  simulated: boolean;
  delivery_id: number;
  invoice: { id: string; status: string; paid_at: number };
  message: string;
}> =>
  adminFetch("/api/admin/test/simulate_payment", {
    method: "POST",
    body: JSON.stringify({ invoice_id: invoiceId }),
  });

export const getWebhookEndpoint = (
  apiKey: string,
): Promise<WebhookEndpoint> =>
  apiKeyFetch<WebhookEndpoint>("/api/v1/webhook_endpoint", apiKey);

export const setWebhookEndpoint = (
  apiKey: string,
  body: { url: string; rotate?: boolean },
): Promise<{
  url: string;
  signing_secret?: string;
  signing_secret_prefix?: string;
  rotated: boolean;
}> =>
  apiKeyFetch("/api/v1/webhook_endpoint", apiKey, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const listWebhookDeliveries = (
  apiKey: string,
): Promise<{ object: "list"; data: WebhookDelivery[]; has_more: boolean }> =>
  apiKeyFetch("/api/v1/webhook_deliveries", apiKey);

export const retryWebhookDelivery = (
  apiKey: string,
  id: number,
): Promise<{ id: number; requeued: true }> =>
  apiKeyFetch(`/api/v1/webhook_deliveries/${id}/retry`, apiKey, {
    method: "POST",
  });

export const listInvoices = (
  apiKey: string,
  opts: { status?: InvoiceStatus; starting_after?: number; limit?: number } = {},
): Promise<{ object: "list"; data: Invoice[]; has_more: boolean }> => {
  const qs = new URLSearchParams();
  if (opts.status) qs.set("status", opts.status);
  if (opts.starting_after) qs.set("starting_after", String(opts.starting_after));
  if (opts.limit) qs.set("limit", String(opts.limit));
  const path = "/api/v1/invoices" + (qs.toString() ? `?${qs}` : "");
  return apiKeyFetch(path, apiKey);
};

/** Sent with the dashboard session, so the shop also gets the shipping address. */
export const getInvoice = (
  publicId: string,
): Promise<Invoice> =>
  adminFetch<Invoice>(`/api/v1/invoices/${encodeURIComponent(publicId)}`);

// ─── V1.5 — Swap-route health ────────────────────────────────────────
// Which "pay with any crypto" coins are routable to settlement right now.
// Public endpoint (no auth); the checkout uses the same data to hide
// coins with no live route so a buyer never hits a dead end.

export interface RouteHealthEntry {
  asset_id: string;
  label: string;
  ticker: string;
  /** Offered to buyers right now (hysteresis applied). */
  offered: boolean;
  /** Raw result of the most recent probe sweep. */
  healthy: boolean;
  consecutive_failures: number;
  /** Winning swap protocol ("near"/"chainflip") or null. */
  protocol: string | null;
  last_checked: number;
  last_healthy: number | null;
  reason: string | null;
}

export interface RouteHealth {
  /** unix ms of the last completed sweep (0 = monitor not warmed yet). */
  updated_at: number;
  settlement_asset: string | null;
  routes: RouteHealthEntry[];
  available: string[];
}

export const getRouteHealth = (): Promise<RouteHealth> =>
  baseFetch<RouteHealth>("/api/v1/health/routes");

// ─── V1.4 — Billing (Free / Pro) ─────────────────────────────────────

export interface Billing {
  tier: "free" | "pro";
  status: "active" | "past_due" | "cancelled";
  renews_at: number | null;
  custom_domain: string | null;
  pro_price_usd: number;
  pro_billing_period: "month";
  /** V1.6 — false at launch: self-serve upgrades are disabled (CTA hidden). */
  pro_upgrades_enabled?: boolean;
  pro_features: string[];
  checkout_bg_url: string | null;
  checkout_bg_scrim: "none" | "dark" | "light";
}

export type Scrim = "none" | "dark" | "light";

/** Pro: upload a custom checkout background (multipart). */
export const uploadCheckoutBg = (
  file: File,
  scrim: Scrim,
): Promise<{ checkout_bg_url: string; checkout_bg_scrim: Scrim }> => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("scrim", scrim);
  return adminFetch("/api/admin/merchants/me/checkout-bg", { method: "POST", body: fd });
};

export const setCheckoutBgScrim = (scrim: Scrim): Promise<{ checkout_bg_scrim: Scrim }> =>
  adminFetch("/api/admin/merchants/me/checkout-bg/scrim", {
    method: "POST",
    body: JSON.stringify({ scrim }),
  });

export const removeCheckoutBg = (): Promise<{ checkout_bg_url: null }> =>
  adminFetch("/api/admin/merchants/me/checkout-bg", { method: "DELETE" });

export const getBilling = (): Promise<Billing> =>
  adminFetch<Billing>("/api/admin/billing");

/** Start a Pro upgrade flow. Returns a `checkout_url` that the merchant
 *  opens in a new tab — the same hosted checkout every customer uses. */
export const upgradeToBilling = (): Promise<{
  checkout_url: string;
  amount_usd_cents: number;
  expires_at: number;
}> =>
  adminFetch("/api/admin/billing/upgrade", {
    method: "POST",
    body: JSON.stringify({}),
  });

export const setCustomDomain = (
  domain: string | null,
): Promise<{ custom_domain: string | null; cname_target?: string; status?: string | null }> =>
  adminFetch("/api/admin/billing/custom_domain", {
    method: "POST",
    body: JSON.stringify({ domain: domain ?? "" }),
  });

/** Poll the Cloudflare custom-hostname provisioning status. */
export const getCustomDomainStatus = (): Promise<{
  custom_domain: string | null;
  cname_target: string;
  status: string | null; // null | pending_validation | active | error
}> => adminFetch("/api/admin/billing/custom_domain/status");

// ─── V1.4 — Pages (tip / donate / fund) ──────────────────────────────

export type PageKind = "tip" | "donate" | "fund";

export interface MerchantPage {
  id: number;
  kind: PageKind;
  slug: string;
  url: string;
  config: PageConfig;
  enabled: boolean;
  created_at: number;
  updated_at: number;
}

/** Discriminated by kind — but at the frontend layer we keep it open
 *  so the merchant form can edit any subset. Backend strictly validates. */
/** Preset social/web links shown on a page. Each is an http(s) URL. */
export interface PageSocial {
  website?: string;
  x?: string;
  instagram?: string;
  telegram?: string;
  youtube?: string;
}

export const PAGE_SOCIAL_FIELDS: Array<{ key: keyof PageSocial; label: string; placeholder: string }> = [
  { key: "website", label: "Website", placeholder: "https://yoursite.com" },
  { key: "x", label: "X (Twitter)", placeholder: "https://x.com/you" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/you" },
  { key: "telegram", label: "Telegram", placeholder: "https://t.me/you" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@you" },
];

/** Discriminated by kind — but at the frontend layer we keep it open
 *  so the merchant form can edit any subset. Backend strictly validates. */
export interface PageConfig {
  title?: string;
  description?: string;
  /** Set by the native image upload (POST .../image); a /u/page-image path. */
  image_url?: string;
  social?: PageSocial;
  suggested_amounts?: number[];
  /** donate / fund only */
  allow_messages?: boolean;
  /** fund only */
  goal_usd_cents?: number;
  deadline_ms?: number;
  leaderboard?: boolean;
}

export const listPages = (): Promise<{
  object: "list";
  data: MerchantPage[];
}> => adminFetch("/api/admin/pages");

export const createPage = (body: {
  kind: PageKind;
  slug: string;
  config: PageConfig;
}): Promise<MerchantPage> =>
  adminFetch<MerchantPage>("/api/admin/pages", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePage = (
  id: number,
  body: { config: PageConfig; enabled?: boolean },
): Promise<MerchantPage> =>
  adminFetch<MerchantPage>(`/api/admin/pages/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deletePage = (id: number): Promise<{ id: number; enabled: false }> =>
  adminFetch(`/api/admin/pages/${id}`, { method: "DELETE" });

// ─── Pay-to-unlock ─────────────────────────────────────────────────────
export interface UnlockPage {
  id: number;
  slug: string;
  url: string;
  price_usd_cents: number;
  title: string | null;
  description: string | null;
  created_at: number;
}
export const listUnlocks = (): Promise<{ object: "list"; data: UnlockPage[] }> =>
  adminFetch("/api/admin/unlocks");

export const createUnlock = (body: {
  price_usd_cents: number;
  payload: string;
  title?: string;
  description?: string;
  aup_accepted: true;
  file?: File | null;
}): Promise<UnlockPage> => {
  if (body.file) {
    const fd = new FormData();
    fd.append("price_usd_cents", String(body.price_usd_cents));
    if (body.payload) fd.append("payload", body.payload);
    if (body.title) fd.append("title", body.title);
    if (body.description) fd.append("description", body.description);
    fd.append("aup_accepted", "true");
    fd.append("file", body.file, body.file.name);
    // No Content-Type header: the browser sets the multipart boundary.
    return adminFetch<UnlockPage>("/api/admin/unlocks", { method: "POST", body: fd });
  }
  return adminFetch<UnlockPage>("/api/admin/unlocks", {
    method: "POST",
    body: JSON.stringify({ ...body, file: undefined }),
  });
};

export const deleteUnlock = (id: number): Promise<{ id: number; enabled: false }> =>
  adminFetch(`/api/admin/unlocks/${id}`, { method: "DELETE" });

/** Upload a page image (PNG/JPEG/WebP, ≤4 MB). Returns the stored URL, which
 *  the backend also writes into the page's config.image_url. */
export const uploadPageImage = (
  id: number,
  file: File,
): Promise<{ id: number; image_url: string }> => {
  const fd = new FormData();
  fd.append("file", file);
  return adminFetch(`/api/admin/pages/${id}/image`, { method: "POST", body: fd });
};

/** Remove a page's image. */
export const deletePageImage = (id: number): Promise<{ id: number; image_url: null }> =>
  adminFetch(`/api/admin/pages/${id}/image`, { method: "DELETE" });

// ─── V1.6 — Ads (platform account only) ──────────────────────────────

export type AdPlacement = "checkout" | "confirming" | "both";

export interface Ad {
  id: number;
  title: string;
  image_url: string | null;
  target_url: string | null;
  html: string | null;
  placement: AdPlacement;
  weight: number;
  enabled: boolean;
  /** Revenue rate — USD cents per 1000 impressions (CPM). */
  cpm_usd_cents: number;
  impressions: number;
  clicks: number;
  /** Server-computed estimated revenue (USD cents) = cpm × impressions / 1000. */
  est_revenue_cents: number;
  created_at: number;
  updated_at: number;
}

export interface AdInput {
  title?: string;
  target_url?: string | null;
  html?: string | null;
  placement: AdPlacement;
  weight?: number;
  enabled?: boolean;
  cpm_usd_cents?: number;
}

export const listAds = (): Promise<{ object: "list"; data: Ad[] }> =>
  adminFetch("/api/admin/ads");

export const createAd = (body: AdInput): Promise<Ad> =>
  adminFetch("/api/admin/ads", { method: "POST", body: JSON.stringify(body) });

export const updateAd = (id: number, body: AdInput): Promise<Ad> =>
  adminFetch(`/api/admin/ads/${id}`, { method: "PUT", body: JSON.stringify(body) });

export const deleteAd = (id: number): Promise<{ id: number; deleted: true }> =>
  adminFetch(`/api/admin/ads/${id}`, { method: "DELETE" });

export const uploadAdImage = (id: number, file: File): Promise<{ id: number; image_url: string }> => {
  const fd = new FormData();
  fd.append("file", file);
  return adminFetch(`/api/admin/ads/${id}/image`, { method: "POST", body: fd });
};
